import { bindJobWorldView } from "../home/job_world_view"
import { Application } from "@hotwired/stimulus"
import NavController from "../controllers/nav_controller"

const DEFAULT_GTAG_ID = "G-PYTW5N1N5K"

let lazyImageObserver
let jobInteractionsPromise
let homeJobsRuntimePromise

function bodyElement() {
  return document.body
}

function homeRoot() {
  return document.querySelector(".home-page")
}

function dataActionIncludes(element, actionName) {
  return element?.dataset?.action?.includes(actionName)
}

function navigateToLink(event, link) {
  if (!link) return

  if (event) {
    if (event.metaKey || event.ctrlKey) {
      window.open(link, "_blank", "noopener,noreferrer")
      return
    }

    event.preventDefault?.()
    event.stopPropagation?.()
  }

  window.location.href = link
}

function installFallbackJobClickHandlers() {
  if (window.__devfoundHomeJobsReady || window.__devfoundHomeFallbackClicksInstalled) return

  window.tableRowClick = (event, link) => {
    navigateToLink(event, link)
  }

  window.tableTurboRowClick = (event, link) => {
    navigateToLink(event, link)
  }

  window.spanTagsClick = (event, link) => {
    navigateToLink(event, link)
  }

  window.__devfoundHomeFallbackClicksInstalled = true
}

function ensureAnalyticsQueue() {
  if (window.__devfoundHomeGtagConfigured) return

  const gtagId = bodyElement()?.dataset.homeGtagId || DEFAULT_GTAG_ID

  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag("js", new Date())
  window.gtag("config", gtagId)
  window.__devfoundHomeGtagConfigured = true
}

function loadHomepageAnalyticsScript() {
  const host = bodyElement()?.dataset.homeGtagHost
  const gtagId = bodyElement()?.dataset.homeGtagId || DEFAULT_GTAG_ID

  if (!host) return Promise.resolve(false)
  if (window.__devfoundHomeGtagScriptPromise) return window.__devfoundHomeGtagScriptPromise

  window.__devfoundHomeGtagScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.async = true
    script.src = `https://${host}/gtag/js?id=${encodeURIComponent(gtagId)}`
    script.dataset.homeAnalyticsScript = "true"
    script.onload = () => resolve(true)
    script.onerror = reject
    document.head.appendChild(script)
  }).catch((error) => {
    window.__devfoundHomeGtagScriptPromise = null
    console.error("Failed to load homepage analytics runtime:", error)
    return false
  })

  return window.__devfoundHomeGtagScriptPromise
}

function scheduleHomepageAnalytics() {
  ensureAnalyticsQueue()

  const host = bodyElement()?.dataset.homeGtagHost
  if (!host || window.__devfoundHomeAnalyticsScheduled) return

  const triggerLoad = () => {
    if (window.__devfoundHomeAnalyticsLoaded) return

    window.__devfoundHomeAnalyticsLoaded = true
    cleanupInteractions()
    loadHomepageAnalyticsScript()
  }

  const interactionEvents = ["pointerdown", "keydown"]
  const cleanupInteractions = () => {
    interactionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, onInteraction, eventListenerOptions(eventName))
    })
  }
  const onInteraction = () => {
    triggerLoad()
  }

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, onInteraction, eventListenerOptions(eventName))
  })

  window.__devfoundHomeAnalyticsScheduled = true
}

function hasCustomAnalyticsEndpoint() {
  return document.querySelector('meta[name="analytics-endpoint"]') !== null
}

function sendToCustomAnalyticsEndpoint(eventData) {
  const endpoint = document.querySelector('meta[name="analytics-endpoint"]')?.content
  if (!endpoint) return

  fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
    },
    body: JSON.stringify(eventData)
  }).catch((error) => {
    console.error("Analytics tracking error:", error)
  })
}

function sendHomepageAnalyticsEvent(eventData) {
  if (typeof gtag !== "undefined") {
    gtag("event", eventData.event, {
      event_category: eventData.section,
      event_label: eventData.text || eventData.benefit,
      event_action: eventData.action,
      page_location: eventData.location
    })
  }

  if (typeof ga !== "undefined") {
    ga("send", "event", {
      eventCategory: eventData.section,
      eventAction: eventData.action,
      eventLabel: eventData.text || eventData.benefit,
      eventValue: 1
    })
  }

  if (hasCustomAnalyticsEndpoint()) {
    sendToCustomAnalyticsEndpoint(eventData)
  }

  if (typeof mixpanel !== "undefined") {
    mixpanel.track(eventData.event, eventData)
  }

  if (typeof analytics !== "undefined" && analytics.track) {
    analytics.track(eventData.event, eventData)
  }
}

function bindAnalyticsSections(root = document) {
  root.querySelectorAll('[data-controller~="analytics"]').forEach((section) => {
    if (section.dataset.analyticsBound === "true") return

    const hoveredBenefitCards = new WeakSet()

    const clickHandler = (event) => {
      const element = event.target.closest('[data-analytics-action], [data-action="click"], [data-event], [data-section], a, button')
      if (!element || !section.contains(element)) return

      const wantsTracking = element.dataset.analyticsAction || element.dataset.action === "click" || element.dataset.event || element.dataset.section
      if (!wantsTracking) return

      const eventData = {
        section: element.dataset.section || section.dataset.analyticsSectionValue,
        action: element.dataset.analyticsAction || element.dataset.action || section.dataset.analyticsActionValue || "click",
        event: element.dataset.event || section.dataset.analyticsEventValue || "element_click",
        location: element.dataset.location || section.dataset.analyticsLocationValue,
        href: element.href || null,
        text: element.textContent.trim(),
        timestamp: new Date().toISOString()
      }

      sendHomepageAnalyticsEvent(eventData)
    }

    const pointerEnterHandler = (event) => {
      const card = event.target.closest("[data-benefit]")
      if (!card || !section.contains(card) || hoveredBenefitCards.has(card)) return

      hoveredBenefitCards.add(card)

      const eventData = {
        section: card.dataset.section || section.dataset.analyticsSectionValue || "post-job-cta",
        action: "hover",
        event: card.dataset.event || "benefit_card_hover",
        benefit: card.dataset.benefit,
        benefitTitle: card.dataset.benefitTitle,
        location: card.dataset.location || section.dataset.analyticsLocationValue || "homepage-section",
        timestamp: new Date().toISOString()
      }

      sendHomepageAnalyticsEvent(eventData)
    }

    section.addEventListener("click", clickHandler)
    section.addEventListener("pointerenter", pointerEnterHandler, true)
    section.dataset.analyticsBound = "true"
  })
}

function bindFlashDismiss() {
  const body = bodyElement()
  if (!body || body.dataset.flashDismissBound === "true") return

  const clickHandler = (event) => {
    const button = event.target.closest('[data-bs-dismiss="alert"]')
    if (!button) return

    event.preventDefault()
    button.closest(".alert")?.remove()
  }

  document.addEventListener("click", clickHandler)
  body.dataset.flashDismissBound = "true"
}

function closeHomeDropdowns(exceptContainer = null) {
  document.querySelectorAll('[data-bs-toggle="dropdown"]').forEach((trigger) => {
    const container = trigger.closest(".dropdown")
    const menu = container?.querySelector(".dropdown-menu")

    if (!container || container === exceptContainer) return

    container.classList.remove("show")
    menu?.classList.remove("show")
    trigger.setAttribute("aria-expanded", "false")
  })
}

function bindDropdowns() {
  const body = bodyElement()
  if (!body || body.dataset.homeDropdownsBound === "true") return

  // When importbootstrap is loaded on the page (logged-in sessions, see
  // application.html.erb), Bootstrap's Dropdown component handles all
  // [data-bs-toggle="dropdown"] clicks natively. Binding the fallback
  // handler below would race against it — both listen on document, both
  // toggle the .show class, and the fallback calls stopPropagation which
  // can swallow other listeners. Skip entirely when the marker is set.
  // importbootstrap.js sets window.__devfoundBootstrapReady synchronously
  // at module top, before DOMContentLoaded fires, so this check is reliable.
  if (typeof window !== "undefined" && window.__devfoundBootstrapReady) return

  const clickHandler = (event) => {
    const trigger = event.target.closest('[data-bs-toggle="dropdown"]')

    if (trigger) {
      const container = trigger.closest(".dropdown")
      const menu = container?.querySelector(".dropdown-menu")
      if (!container || !menu) return

      event.preventDefault()
      event.stopPropagation()

      const isOpen = container.classList.contains("show")
      closeHomeDropdowns(isOpen ? null : container)
      container.classList.toggle("show", !isOpen)
      menu.classList.toggle("show", !isOpen)
      trigger.setAttribute("aria-expanded", isOpen ? "false" : "true")
      return
    }

    if (!event.target.closest(".dropdown-menu")) {
      closeHomeDropdowns()
    }
  }

  const keyHandler = (event) => {
    if (event.key === "Escape") {
      closeHomeDropdowns()
    }
  }

  document.addEventListener("click", clickHandler)
  document.addEventListener("keydown", keyHandler)
  body.dataset.homeDropdownsBound = "true"
}

function loadJobInteractions() {
  if (jobInteractionsPromise) return jobInteractionsPromise

  jobInteractionsPromise = import(/* webpackChunkName: "home-job-interactions" */ "./positivius_job_interactions")
    .then(({ initPositiviusJobInteractions }) => {
      initPositiviusJobInteractions?.()
    })
    .catch((error) => {
      jobInteractionsPromise = null
      console.error("Failed to load homepage job interactions:", error)
    })

  return jobInteractionsPromise
}

function scheduleJobInteractions() {
  const load = () => {
    loadJobInteractions()
  }

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(load, { timeout: 1500 })
  } else {
    window.setTimeout(load, 300)
  }
}

function loadHomeJobsRuntime() {
  if (homeJobsRuntimePromise) return homeJobsRuntimePromise

  homeJobsRuntimePromise = import(/* webpackChunkName: "home-jobs-runtime" */ "../home/jobs_runtime")
    .then(({ initHomeJobsRuntime }) => initHomeJobsRuntime())
    .catch((error) => {
      homeJobsRuntimePromise = null
      console.error("Failed to load homepage jobs runtime:", error)
    })

  return homeJobsRuntimePromise
}

function scheduleHomeJobsRuntime() {
  const root = document.querySelector(".modern-jobs-interface-wrapper")
  if (!root || root.dataset.jobsRuntimeObserved === "true") return

  const triggerLoad = () => {
    root._jobsRuntimeObserver?.disconnect()
    root._jobsRuntimeCleanup?.()
    delete root._jobsRuntimeObserver
    delete root._jobsRuntimeCleanup
    delete root.dataset.jobsRuntimeObserved
    loadHomeJobsRuntime()
  }

  const interactionHandler = () => {
    triggerLoad()
  }

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          triggerLoad()
        }
      })
    }, {
      rootMargin: "300px 0px"
    })

    root._jobsRuntimeObserver = observer
    observer.observe(root)
  } else {
    window.setTimeout(triggerLoad, 300)
  }

  root.addEventListener("pointerdown", interactionHandler, { once: true, passive: true })
  root.addEventListener("focusin", interactionHandler, { once: true })
  root._jobsRuntimeCleanup = () => {
    root.removeEventListener("pointerdown", interactionHandler)
    root.removeEventListener("focusin", interactionHandler)
  }
  root.dataset.jobsRuntimeObserved = "true"
}

function eventListenerOptions(eventName) {
  return eventName === "scroll" || eventName === "pointerdown" ? { once: true, passive: true } : { once: true }
}

function initHeaderScroll() {
  const header = document.querySelector(".modern-header")
  if (!header) return

  if (window.__devfoundHomeHeaderScrollBound) {
    window.removeEventListener("scroll", window.__devfoundHomeHeaderScrollBound)
  }

  let ticking = false

  const updateHeader = () => {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0
    header.classList.toggle("scrolled", scrollY > 10)
    ticking = false
  }

  const requestTick = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateHeader)
      ticking = true
    }
  }

  window.__devfoundHomeHeaderScrollBound = requestTick
  window.addEventListener("scroll", requestTick, { passive: true })
  updateHeader()
}

function loadLazyImage(image) {
  if (!image || image.dataset.lazyLoaded === "true") return

  const source = image.dataset.src
  const sourceSet = image.dataset.srcset

  if (sourceSet) {
    image.srcset = sourceSet
  }

  if (source) {
    image.src = source
  }

  image.dataset.lazyLoaded = "true"
  image.classList.remove("lazy")

  image.addEventListener("load", () => {
    image.classList.add("loaded")
  }, { once: true })
}

function initLazyImages(root = document) {
  const images = Array.from(root.querySelectorAll("img[data-src], img[data-srcset]"))
    .filter((image) => image.dataset.lazyObserved !== "true")

  if (!images.length) return

  if (!("IntersectionObserver" in window)) {
    images.forEach(loadLazyImage)
    return
  }

  if (!lazyImageObserver) {
    lazyImageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        loadLazyImage(entry.target)
        lazyImageObserver.unobserve(entry.target)
      })
    }, {
      rootMargin: "200px 0px"
    })
  }

  images.forEach((image) => {
    image.dataset.lazyObserved = "true"
    lazyImageObserver.observe(image)
  })
}

function activatePlanRole(section, role) {
  if (!role) return

  section.dataset.planTabsActiveValue = role

  section.querySelectorAll('[data-plan-tabs-target="panel"]').forEach((panel) => {
    panel.classList.toggle("hidden", panel.dataset.panel !== role)
  })

  section.querySelectorAll('[data-plan-tabs-target="tab"]').forEach((tab) => {
    const isActive = tab.dataset.role === role
    const pill = tab.querySelector("[data-plan-tab-pill]") || tab

    pill.classList.toggle("bg-white", isActive)
    pill.classList.toggle("text-[#191A23]", isActive)
    pill.classList.toggle("shadow-sm", isActive)
    pill.classList.toggle("text-[#191A23]/60", !isActive)
    tab.setAttribute("aria-pressed", String(isActive))
  })
}

function updateTagsExpandToggle(toggleButton, extraCount, isExpanded) {
  const visibleText = isExpanded ? "Less" : `+${extraCount} more`
  const accessibleName = isExpanded ? "Less filters" : `+${extraCount} more filters`
  const arrow = isExpanded ? "▲" : "▼"

  toggleButton.innerHTML = `<span class="expand-text">${visibleText}</span> <span class="expand-arrow">${arrow}</span>`
  toggleButton.setAttribute("aria-label", accessibleName)
  toggleButton.setAttribute("aria-expanded", String(isExpanded))
}

function filterButtons(section) {
  return Array.from(section.querySelectorAll(".filter-btn[data-url]"))
}

function setSelectedButtonState(button, isSelected) {
  button.classList.toggle("selected", isSelected)
  button.classList.toggle("active", isSelected)
  button.classList.toggle("unselected", !isSelected)
}

function syncRegionSelection(section, nextRegion = "") {
  section.querySelectorAll("[data-region]").forEach((button) => {
    const isSelected = (button.dataset.region || "") === (nextRegion || "")
    setSelectedButtonState(button, isSelected)
  })
}

function syncTagSelection(section, pathname) {
  const normalizedPathname = pathname || new URL(window.location.href).pathname
  let hasMatch = false

  filterButtons(section).forEach((button) => {
    const buttonPath = new URL(button.dataset.url, window.location.origin).pathname
    const isSelected = buttonPath === normalizedPathname
    if (isSelected) hasMatch = true
    setSelectedButtonState(button, isSelected)
  })

  if (!hasMatch) {
    const allAiButton = section.querySelector('.filter-btn[data-url="/ai-jobs"]')
    if (allAiButton) setSelectedButtonState(allAiButton, true)
  }
}

function syncFilterUiFromLocation(section) {
  const currentUrl = new URL(window.location.href)
  syncRegionSelection(section, currentUrl.searchParams.get("region") || "")
  syncTagSelection(section, currentUrl.pathname)
}

function navigateJobResults(url) {
  const frame = document.querySelector("turbo-frame#job_results")
  const nextUrl = new URL(url, window.location.origin)

  if (frame) {
    frame.dispatchEvent(new CustomEvent("devfound:filter-loading-start", { bubbles: true }))
  }

  if (frame && window.Turbo && typeof window.Turbo.visit === "function") {
    window.Turbo.visit(nextUrl.toString(), { frame: frame.id, action: "advance" })
    return
  }

  if (frame) {
    frame.src = nextUrl.toString()
    return
  }

  window.location.href = nextUrl.toString()
}

function applySearchFilter(searchInput) {
  if (!searchInput) return

  const destinationUrl = new URL(window.location.href)
  const searchValue = searchInput.value.trim()

  if (searchValue) {
    destinationUrl.searchParams.set("search", searchValue)
  } else {
    destinationUrl.searchParams.delete("search")
  }

  destinationUrl.searchParams.delete("page")
  navigateJobResults(destinationUrl.toString())
}

function syncSearchClearState(section) {
  const searchInput = section.querySelector('[data-job-filters-target="searchInput"]')
  const clearButton = section.querySelector('[data-job-filters-target="clearSearch"]')
  if (!searchInput || !clearButton) return

  if (searchInput.value.trim()) {
    clearButton.classList.remove("hidden")
  } else {
    clearButton.classList.add("hidden")
  }
}

function bindPlanTabs(root = document) {
  root.querySelectorAll('[data-controller~="plan-tabs"]').forEach((section) => {
    if (section.dataset.planTabsBound === "true") return

    const defaultRole = section.dataset.planTabsActiveValue || section.querySelector('[data-plan-tabs-target="panel"]')?.dataset.panel
    if (defaultRole) {
      activatePlanRole(section, defaultRole)
    }

    const clickHandler = (event) => {
      const tab = event.target.closest('[data-plan-tabs-target="tab"]')
      if (!tab || !section.contains(tab)) return

      event.preventDefault()
      activatePlanRole(section, tab.dataset.role)
    }

    section.addEventListener("click", clickHandler)
    section.dataset.planTabsBound = "true"
  })
}

function toggleExtraTags(section) {
  const container = section.querySelector('[data-job-filters-target="tagsContainer"]')
  const toggleButton = section.querySelector('[data-job-filters-target="expandToggle"]')
  if (!container || !toggleButton) return

  const isExpanded = toggleButton.classList.toggle("expanded")
  const extras = Array.from(container.querySelectorAll(".extra-filter-tag"))

  updateTagsExpandToggle(toggleButton, extras.length, isExpanded)

  if (isExpanded) {
    extras.forEach((element) => {
      element.classList.remove("hidden")
    })
  } else {
    extras.forEach((element) => {
      element.classList.add("hidden")
    })
  }
}

function bindJobFilters(root = document) {
  root.querySelectorAll('[data-controller~="job-filters"]').forEach((section) => {
    if (section.dataset.jobFiltersBound === "true") return

    let searchTimer = null

    const clickHandler = (event) => {
      const regionButton = event.target.closest("[data-region]")
      if (regionButton && dataActionIncludes(regionButton, "job-filters#handleRegionChange")) {
        event.preventDefault()
        const destinationUrl = new URL(window.location.href)
        const region = regionButton.dataset.region

        if (region) {
          destinationUrl.searchParams.set("region", region)
        } else {
          destinationUrl.searchParams.delete("region")
        }

        destinationUrl.searchParams.delete("page")
        syncRegionSelection(section, region || "")
        navigateJobResults(destinationUrl.toString())
        return
      }

      const searchSubmit = event.target.closest('[data-job-filters-target="searchSubmit"]')
      if (searchSubmit && dataActionIncludes(searchSubmit, "job-filters#applySearch")) {
        event.preventDefault()
        const searchInput = section.querySelector('[data-job-filters-target="searchInput"]')
        applySearchFilter(searchInput)
        return
      }

      const clearSearch = event.target.closest('[data-job-filters-target="clearSearch"]')
      if (clearSearch && dataActionIncludes(clearSearch, "job-filters#clearSearch")) {
        event.preventDefault()
        const searchInput = section.querySelector('[data-job-filters-target="searchInput"]')
        if (!searchInput) return

        searchInput.value = ""
        syncSearchClearState(section)
        searchInput.focus()
        applySearchFilter(searchInput)
        return
      }

      const tagButton = event.target.closest("[data-url]")
      if (tagButton && dataActionIncludes(tagButton, "job-filters#handleTagChange")) {
        event.preventDefault()
        const currentUrl = new URL(window.location.href)
        const targetUrl = new URL(tagButton.dataset.url, window.location.origin)
        const region = currentUrl.searchParams.get("region")

        if (region) {
          targetUrl.searchParams.set("region", region)
        }

        ["salary", "benefits", "estimated", "sort", "search"].forEach((param) => {
          const value = currentUrl.searchParams.get(param)
          if (value) {
            targetUrl.searchParams.set(param, value)
          }
        })

        targetUrl.searchParams.delete("page")
        syncTagSelection(section, targetUrl.pathname)
        navigateJobResults(targetUrl.toString())
        return
      }

      const expandToggle = event.target.closest('[data-job-filters-target="expandToggle"]')
      if (expandToggle && dataActionIncludes(expandToggle, "job-filters#toggleTagsExpansion")) {
        event.preventDefault()
        toggleExtraTags(section)
      }
    }

    const inputHandler = (event) => {
      const searchInput = event.target.closest('[data-job-filters-target="searchInput"]')
      if (!searchInput) return

      window.clearTimeout(searchTimer)
      searchTimer = window.setTimeout(() => {
        syncSearchClearState(section)
      }, 60)
    }

    const keydownHandler = (event) => {
      const searchInput = event.target.closest('[data-job-filters-target="searchInput"]')
      if (!searchInput) return

      if (event.key === "Enter") {
        event.preventDefault()
        applySearchFilter(searchInput)
      }
    }

    const changeHandler = (event) => {
      const sortSelect = event.target.closest('[data-job-filters-target="sortSelect"]')
      if (!sortSelect) return

      const destinationUrl = new URL(window.location.href)
      const sortValue = sortSelect.value

      if (sortValue && sortValue !== "newest") {
        destinationUrl.searchParams.set("sort", sortValue)
      } else {
        destinationUrl.searchParams.delete("sort")
      }

      destinationUrl.searchParams.delete("page")
      navigateJobResults(destinationUrl.toString())
    }

    section.addEventListener("click", clickHandler)
    section.addEventListener("input", inputHandler)
    section.addEventListener("keydown", keydownHandler)
    section.addEventListener("change", changeHandler)
    syncSearchClearState(section)
    syncFilterUiFromLocation(section)
    section.dataset.jobFiltersBound = "true"
  })
}

function bindLoadMore(root = document) {
  root.querySelectorAll('[data-controller~="load-more"]').forEach((section) => {
    if (section.dataset.loadMoreBound === "true") return

    const clickHandler = (event) => {
      const button = event.target.closest('[data-action*="load-more#loadMore"]')
      if (!button || !section.contains(button)) return

      event.preventDefault()
      event.stopPropagation()

      if (section.dataset.loadingValue === "true") return

      section.dataset.loadingValue = "true"
      button.disabled = true
      button.classList.add("loading")

      const textTarget = section.querySelector('[data-load-more-target="text"]')
      const arrowTarget = section.querySelector('[data-load-more-target="arrow"]')
      if (textTarget) {
        textTarget.textContent = "Loading Jobs..."
      }
      if (arrowTarget) {
        arrowTarget.style.animation = "spin 0.5s linear infinite"
      }

      const destinationUrl = new URL(window.location.href)
      destinationUrl.pathname = "/ai-jobs"
      const currentPage = Number.parseInt(destinationUrl.searchParams.get("page") || section.dataset.loadMorePageValue || "1", 10)
      destinationUrl.searchParams.set("page", currentPage + 1)
      destinationUrl.searchParams.delete("load_more")
      window.location.href = destinationUrl.toString()
    }

    section.addEventListener("click", clickHandler)
    section.dataset.loadMoreBound = "true"
  })
}

function resetFaqItem(item) {
  const answer = item.querySelector(".homepage-faq-answer")
  const trigger = item.querySelector(".homepage-faq-question-wrapper")

  if (!answer || !trigger) return

  item.dataset.expanded = "false"
  answer.setAttribute("aria-hidden", "true")
  answer.style.maxHeight = "0px"
  trigger.setAttribute("aria-expanded", "false")
}

function faqParts(item) {
  const answer = item.querySelector(".homepage-faq-answer")

  return {
    answer,
    content: answer?.firstElementChild,
    trigger: item.querySelector(".homepage-faq-question-wrapper")
  }
}

function cacheFaqHeight(item, height) {
  const { answer } = faqParts(item)
  if (!answer) return 0

  const expandedHeight = Math.max(Math.ceil(height), 0)
  answer.dataset.expandedHeight = `${expandedHeight}`

  if (item.dataset.expanded === "true") {
    answer.style.maxHeight = `${expandedHeight}px`
  }

  return expandedHeight
}

function measureFaqHeight(item) {
  const { content } = faqParts(item)
  if (!content) return 0

  return cacheFaqHeight(item, content.scrollHeight)
}

function faqExpandedHeight(item) {
  const { answer } = faqParts(item)
  const storedHeight = Number.parseFloat(answer?.dataset.expandedHeight || "0")

  if (storedHeight > 0) return storedHeight

  return measureFaqHeight(item)
}

function expandFaq(item) {
  const { answer, trigger } = faqParts(item)
  if (!answer || !trigger) return

  item.dataset.expanded = "true"
  answer.setAttribute("aria-hidden", "false")
  trigger.setAttribute("aria-expanded", "true")
  answer.style.maxHeight = `${faqExpandedHeight(item)}px`
}

function collapseFaq(item) {
  const { answer, trigger } = faqParts(item)
  if (!answer || !trigger) return

  if (item.dataset.expanded !== "true") {
    resetFaqItem(item)
    return
  }

  item.dataset.expanded = "false"
  answer.setAttribute("aria-hidden", "true")
  trigger.setAttribute("aria-expanded", "false")
  answer.style.maxHeight = "0px"
}

function bindFaq(section) {
  if (!section || section.dataset.faqBound === "true") return

  const items = Array.from(section.querySelectorAll(".homepage-faq-item"))
  const faqContents = items
    .map((item) => faqParts(item).content)
    .filter(Boolean)

  items.forEach(resetFaqItem)
  items.forEach(measureFaqHeight)

  const clickHandler = (event) => {
    const trigger = event.target.closest("[data-faq-toggle]")
    if (!trigger || !section.contains(trigger)) return

    const item = trigger.closest(".homepage-faq-item")
    if (!item) return

    event.preventDefault()

    const isExpanded = item.dataset.expanded === "true"

    items.forEach((faqItem) => {
      if (faqItem !== item) {
        collapseFaq(faqItem)
      }
    })

    if (isExpanded) {
      collapseFaq(item)
    } else {
      expandFaq(item)
    }
  }

  const keyHandler = (event) => {
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return

    const currentTrigger = event.target.closest(".homepage-faq-question-wrapper")
    if (!currentTrigger) return

    const currentItem = currentTrigger.closest(".homepage-faq-item")
    const currentIndex = items.indexOf(currentItem)
    if (currentIndex === -1) return

    event.preventDefault()

    const nextIndex = event.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1
    items[nextIndex]?.querySelector(".homepage-faq-question-wrapper")?.focus()
  }

  section.addEventListener("click", clickHandler)
  section.addEventListener("keydown", keyHandler)
  section.dataset.faqBound = "true"

  let faqResizeObserver = null
  let resizeFrame = null
  let resizeHandler = null

  if ("ResizeObserver" in window && faqContents.length > 0) {
    faqResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const item = entry.target.closest(".homepage-faq-item")
        if (!item) return

        cacheFaqHeight(item, entry.contentRect.height)
      })
    })

    faqContents.forEach((content) => faqResizeObserver.observe(content))
  } else {
    resizeHandler = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame)
      }

      resizeFrame = window.requestAnimationFrame(() => {
        items.forEach(measureFaqHeight)
        resizeFrame = null
      })
    }

    window.addEventListener("resize", resizeHandler)
  }

  section._faqCleanup = () => {
    section.removeEventListener("click", clickHandler)
    section.removeEventListener("keydown", keyHandler)
    faqResizeObserver?.disconnect()
    if (resizeFrame) {
      window.cancelAnimationFrame(resizeFrame)
    }
    if (resizeHandler) {
      window.removeEventListener("resize", resizeHandler)
    }
    delete section._faqCleanup
    delete section.dataset.faqBound
  }
}

function initFaqWhenVisible() {
  const section = document.querySelector(".homepage-faq-section")
  if (!section || section.dataset.faqObserved === "true") return

  if (!("IntersectionObserver" in window)) {
    bindFaq(section)
    return
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      bindFaq(section)
      observer.disconnect()
      delete section._faqVisibilityObserver
      delete section.dataset.faqObserved
    })
  }, {
    rootMargin: "150px 0px"
  })

  section.dataset.faqObserved = "true"
  section._faqVisibilityObserver = observer
  observer.observe(section)
}

function teardownFaq() {
  document.querySelectorAll(".homepage-faq-section").forEach((section) => {
    section._faqCleanup?.()
    section._faqVisibilityObserver?.disconnect()
    delete section._faqVisibilityObserver
    delete section.dataset.faqObserved
  })
}

function teardownJobsRuntimeObserver() {
  document.querySelectorAll("[data-jobs-runtime-observed]").forEach((root) => {
    root._jobsRuntimeObserver?.disconnect()
    root._jobsRuntimeCleanup?.()
    delete root._jobsRuntimeObserver
    delete root._jobsRuntimeCleanup
    delete root.dataset.jobsRuntimeObserved
  })
}

function initHeroReveal() {
  const title = document.querySelector("[data-hero-title]");
  if (!title || title.dataset.heroRevealBound) return;
  title.dataset.heroRevealBound = "true";
  const words = title.querySelectorAll(".dfh-word");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // Transform-only reveal: the words stay fully opaque so the <h1> remains an
  // LCP candidate (opacity:0 would disqualify it and hurt LCP on this branch).
  words.forEach((w, i) => {
    if (reduce) { w.style.transform = "none"; return; }
    w.style.transform = "translateY(8px)";
    w.style.transition = "transform .5s ease";
    w.style.transitionDelay = `${i * 45}ms`;
  });
  if (reduce) return;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    words.forEach((w) => { w.style.transform = "none"; });
  }));
}

function initHeroUpload() {
  const card = document.querySelector("[data-hero-upload]");
  if (!card || card.dataset.heroUploadBound) return;
  card.dataset.heroUploadBound = "true";
  const redirect = card.dataset.uploadRedirect || "/ai-resume-scanner";
  const existing = card.querySelector("input[data-hero-file]");
  if (existing) existing.remove();
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".pdf,.doc,.docx";
  input.style.display = "none";
  input.dataset.heroFile = "true";
  card.appendChild(input);
  let triggered = false;
  function go() {
    if (triggered) return;
    triggered = true;
    card.classList.add("is-loading");
    setTimeout(() => { window.location.href = redirect; }, 1200);
  }
  card.addEventListener("click", (e) => {
    if (e.target === input) return;
    e.preventDefault();
    input.click();
  });
  card.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.click(); }
  });
  input.addEventListener("change", () => { if (input.files.length) go(); });
  card.addEventListener("dragover", (e) => { e.preventDefault(); card.classList.add("is-drag-over"); });
  card.addEventListener("dragleave", () => card.classList.remove("is-drag-over"));
  card.addEventListener("drop", (e) => {
    e.preventDefault();
    card.classList.remove("is-drag-over");
    if (e.dataTransfer && e.dataTransfer.files.length) go();
  });
}

function initHeroShowcase() {
  const wrap = document.querySelector("[data-hero-showcase]");
  if (!wrap || wrap.dataset.heroShowcaseBound) return;
  wrap.dataset.heroShowcaseBound = "true";
  const tabs = Array.from(wrap.querySelectorAll("[data-hero-tab]"));
  const panels = Array.from(wrap.querySelectorAll("[data-hero-panel]"));
  if (!tabs.length) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const duration = (parseFloat(wrap.dataset.showcaseDuration) || 15) * 1000;
  wrap.style.setProperty("--dfh-tab-dur", `${duration / 1000}s`);
  let current = 0;
  let timer = null;

  function activate(index) {
    tabs.forEach((t, i) => t.setAttribute("aria-selected", i === index ? "true" : "false"));
    panels.forEach((p, i) => p.classList.toggle("is-active", i === index));
    const active = tabs[index];
    active.classList.remove("is-progress");
    void active.offsetWidth;
    active.classList.add("is-progress");
    current = index;
  }
  function schedule() {
    if (reduce) return;
    clearTimeout(timer);
    timer = setTimeout(() => activateAndSchedule((current + 1) % tabs.length), duration);
  }
  function activateAndSchedule(index) { activate(index); schedule(); }

  tabs.forEach((tab, i) => {
    tab.addEventListener("click", () => { if (i !== current) activateAndSchedule(i); });
    tab.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); activateAndSchedule((i + 1) % tabs.length); tabs[(i + 1) % tabs.length].focus(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); const n = (i - 1 + tabs.length) % tabs.length; activateAndSchedule(n); tabs[n].focus(); }
      else if (e.key === "Home") { e.preventDefault(); activateAndSchedule(0); tabs[0].focus(); }
      else if (e.key === "End") { e.preventDefault(); activateAndSchedule(tabs.length - 1); tabs[tabs.length - 1].focus(); }
    });
  });

  wrap._heroShowcaseStop = () => clearTimeout(timer);
  activateAndSchedule(0);
}

function initSocialProof() {
  const root = document.querySelector("[data-social-proof]")
  if (!root || root.dataset.socialProofBound) return
  root.dataset.socialProofBound = "true"
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches

  // Build the decorative dot grid (100 dots; last few faded) once.
  const grid = root.querySelector("[data-dot-grid]")
  if (grid && !grid.children.length) {
    for (let i = 0; i < 100; i++) {
      const d = document.createElement("i")
      if (i >= 97) d.className = "is-faded"
      grid.appendChild(d)
    }
  }

  // Testimonial carousel: auto-rotate every 7s, prev/next arrows, pause on hover.
  const car = root.querySelector("[data-testimonial-carousel]")
  if (car && !car.dataset.tcBound) {
    car.dataset.tcBound = "true"
    const track = car.querySelector("[data-tc-track]")
    const slides = track ? Array.from(track.children) : []
    if (slides.length > 1) {
      let idx = 0
      let timer = null
      const go = (i) => {
        idx = (i + slides.length) % slides.length
        track.style.transform = "translateX(" + (-idx * 100) + "%)"
        slides.forEach((s, j) => s.setAttribute("aria-hidden", j === idx ? "false" : "true"))
      }
      const stop = () => { if (timer) { clearInterval(timer); timer = null } }
      const start = () => { if (reduce) return; stop(); timer = setInterval(() => go(idx + 1), 7000) }
      const next = car.querySelector("[data-tc-next]")
      const prev = car.querySelector("[data-tc-prev]")
      if (next) next.addEventListener("click", () => { go(idx + 1); start() })
      if (prev) prev.addEventListener("click", () => { go(idx - 1); start() })
      car.addEventListener("mouseenter", stop)
      car.addEventListener("mouseleave", start)
      go(0)
      start()
      root._tcStop = stop
    }
  }

  function countUp(el) {
    const target = parseInt(el.dataset.countTo, 10)
    const suffix = el.dataset.countSuffix || ""
    if (!target) return
    if (reduce) { el.textContent = target.toLocaleString() + suffix; return }
    const dur = 1200
    const start = performance.now()
    function tick(now) {
      const p = Math.min(1, (now - start) / dur)
      const eased = 1 - Math.pow(1 - p, 3)
      el.textContent = Math.floor(target * eased).toLocaleString() + suffix
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  const reveals = Array.from(root.querySelectorAll(".dfp-reveal"))
  if (reduce || !("IntersectionObserver" in window)) {
    reveals.forEach((r) => r.classList.add("is-in"))
    root.querySelectorAll("[data-count-to]").forEach(countUp)
    return
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      entry.target.classList.add("is-in")
      entry.target.querySelectorAll("[data-count-to]").forEach(countUp)
      io.unobserve(entry.target)
    })
  }, { threshold: 0.2 })
  reveals.forEach((r) => io.observe(r))
  root._socialProofIO = io
}

function initInAction() {
  const root = document.querySelector("[data-in-action]")
  if (!root || root.dataset.inActionBound) return
  root.dataset.inActionBound = "true"
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const tabs = Array.from(root.querySelectorAll(".dfa-tab"))
  const panels = Array.from(root.querySelectorAll(".dfa-panel"))
  const resume = root.querySelector("[data-dfa-resume]")
  const DUR = (parseFloat(root.dataset.inActionDur) || 8) * 1000
  root.style.setProperty("--dfa-dur", DUR / 1000 + "s")
  let idx = Math.max(0, tabs.findIndex((t) => t.getAttribute("aria-selected") === "true"))
  let timer = null
  let inView = false
  let hovered = false
  let focused = false
  const rafs = new Set()
  let typeTimer = null

  const cancelAnims = () => { rafs.forEach(cancelAnimationFrame); rafs.clear(); if (typeTimer) clearTimeout(typeTimer) }

  const showPanel = (i) => {
    tabs.forEach((t, j) => t.setAttribute("aria-selected", j === i ? "true" : "false"))
    panels.forEach((p, j) => p.classList.toggle("is-active", j === i))
  }
  const restartProgress = () => {
    // Re-trigger the CSS progress animation on the active tab.
    root.classList.remove("dfa-run"); void root.offsetWidth; if (!reduce) root.classList.add("dfa-run")
  }
  const setup = (i) => {
    cancelAnims()
    idx = (i + tabs.length) % tabs.length
    showPanel(idx)
    resetScore(); resetMatch()
    const id = tabs[idx].dataset.dfaTab
    if (id === "score") revealScore()
  }
  const stop = () => { if (timer) { clearTimeout(timer); timer = null } root.classList.add("dfa-paused") }
  const start = () => {
    stop()
    if (reduce || !inView || hovered || focused) return
    root.classList.remove("dfa-paused")
    restartProgress()
    timer = setTimeout(() => go(idx + 1), DUR)
  }
  const go = (i) => { setup(i); start() }

  tabs.forEach((t, i) => t.addEventListener("click", () => go(i)))
  root.addEventListener("mouseenter", () => { hovered = true; stop() })
  root.addEventListener("mouseleave", () => { hovered = false; start() })
  // Pause auto-advance while any control inside the section has focus (WCAG 2.2.2).
  root.addEventListener("focusin", () => { focused = true; stop() })
  root.addEventListener("focusout", (e) => { if (!root.contains(e.relatedTarget)) { focused = false; start() } })
  // Learn-more links live inside the role=tab divs; don't let their clicks re-trigger the tab.
  root.querySelectorAll(".dfa-learn").forEach((a) => a.addEventListener("click", (e) => e.stopPropagation()))

  // ----- Keyboard navigation between tabs (arrows move; Enter/Space activate) -----
  const tablist = root.querySelector('[role="tablist"]')
  if (tablist) {
    tablist.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        const t = e.target.closest(".dfa-tab")
        if (t) { e.preventDefault(); go(tabs.indexOf(t)) }
        return
      }
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return
      e.preventDefault()
      const next = (idx + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length
      go(next)
      tabs[next].focus()
    })
  }

  // ----- Build: customize -----
  root.querySelectorAll("[data-dfa-swatch]").forEach((sw) => {
    sw.addEventListener("click", () => {
      root.querySelectorAll("[data-dfa-swatch]").forEach((s) => s.setAttribute("aria-pressed", "false"))
      sw.setAttribute("aria-pressed", "true")
      if (resume) resume.style.setProperty("--dfa-accent", sw.dataset.color)
    })
  })
  root.querySelectorAll("[data-dfa-toggle]").forEach((tog) => {
    tog.addEventListener("click", () => {
      const on = tog.getAttribute("aria-pressed") !== "true"
      tog.setAttribute("aria-pressed", String(on))
      const target = tog.dataset.dfaToggle
      if (target === "pic") root.querySelector(".dfa-photo")?.classList.toggle("is-on", on)
      if (target === "dividers") root.querySelectorAll(".dfa-div").forEach((d) => d.classList.toggle("is-on", on))
    })
  })

  // ----- Score: gauge + rings + improve-bullet -----
  const scoreEl = root.querySelector("[data-dfa-score]")
  const scoreLabelEl = root.querySelector("[data-dfa-score-label]")
  const scoreBase = scoreEl ? parseInt(scoreEl.textContent, 10) || 0 : 0
  let scoreCur = scoreBase
  const rings = Array.from(root.querySelectorAll("[data-dfa-ring]"))
  const needleEl = root.querySelector("[data-dfa-needle]")
  const angleFor = (v) => (v / 100) * 180 - 90
  const setNeedle = (v) => { if (needleEl) needleEl.style.transform = "rotate(" + angleFor(v) + "deg)" }
  const labelFor = (v) => v >= 85 ? "Excellent" : v >= 70 ? "Strong" : "Needs improvement"
  const countTo = (el, to, suffix) => {
    if (reduce) { el.textContent = to + (suffix || ""); return }
    const from = parseInt(el.textContent, 10) || 0, t0 = performance.now(), dur = 900
    let id
    const tick = (now) => {
      rafs.delete(id)
      const p = Math.min(1, (now - t0) / dur), v = Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3)))
      el.textContent = v + (suffix || "")
      if (p < 1) { id = requestAnimationFrame(tick); rafs.add(id) }
    }
    id = requestAnimationFrame(tick); rafs.add(id)
  }
  function resetScore() {
    if (!scoreEl) return
    scoreCur = scoreBase; scoreEl.textContent = "0"
    setNeedle(0)
    if (scoreLabelEl) scoreLabelEl.textContent = labelFor(scoreBase)
    rings.forEach((r) => { const n = r.querySelector("[data-dfa-ring-num]"); if (n) n.textContent = "0" })
    root.querySelectorAll("[data-dfa-improve]").forEach((s) => {
      s.classList.remove("is-improved"); if (s.dataset.original) s.textContent = s.dataset.original
    })
  }
  function revealScore() {
    if (!scoreEl) return
    countTo(scoreEl, scoreBase)
    setNeedle(scoreBase)
    if (scoreLabelEl) scoreLabelEl.textContent = labelFor(scoreBase)
    rings.forEach((r) => { const n = r.querySelector("[data-dfa-ring-num]"); if (n) countTo(n, parseInt(r.dataset.val, 10) || 0) })
  }
  root.querySelectorAll("[data-dfa-improve]").forEach((s) => {
    s.dataset.original = s.textContent
    s.addEventListener("click", () => {
      if (s.classList.contains("is-improved")) return
      s.classList.add("is-improved"); s.textContent = s.dataset.improved || s.textContent
      hideCursor()
      const delta = parseInt(s.dataset.delta, 10) || 0
      scoreCur = Math.min(100, scoreCur + delta)
      if (scoreEl) countTo(scoreEl, scoreCur)
      setNeedle(scoreCur)
      if (scoreLabelEl) scoreLabelEl.textContent = labelFor(scoreCur)
    })
  })

  // ----- Match: add keyword bullet -----
  const addBtn = root.querySelector("[data-dfa-add]")
  function resetMatch() {
    root.querySelectorAll(".dfa-bullet").forEach((b) => {
      b.classList.remove("is-on")
      const t = b.querySelector("[data-dfa-bullet-text]"); if (t && t.dataset.full) t.textContent = t.dataset.full
    })
    if (addBtn) addBtn.disabled = false
  }
  if (addBtn) addBtn.addEventListener("click", () => {
    const row = root.querySelector("[data-dfa-missing] [data-dfa-row]")
    if (!row) return
    const kw = row.dataset.keyword
    const bullet = root.querySelector(`.dfa-bullet[data-dfa-bullet="${kw}"]`)
    if (!bullet) return
    addBtn.disabled = true
    bullet.classList.add("is-on")
    const textEl = bullet.querySelector("[data-dfa-bullet-text]")
    if (!textEl) return
    const full = textEl.dataset.full || textEl.textContent
    textEl.dataset.full = full
    if (reduce) { textEl.textContent = full; return }
    textEl.textContent = ""
    let i = 0
    const type = () => { textEl.textContent = full.slice(0, ++i); if (i < full.length) typeTimer = setTimeout(type, 18) }
    type()
  })

  // ----- Follower cursor (Score, pointer devices) -----
  const cursor = root.querySelector("[data-dfa-cursor]")
  const stage = root.querySelector(".dfa-stage")
  function hideCursor() { if (cursor) cursor.style.opacity = "0" }
  if (cursor && stage && !reduce) {
    stage.addEventListener("pointermove", (e) => {
      if (tabs[idx]?.dataset.dfaTab !== "score") return
      const r = stage.getBoundingClientRect()
      cursor.style.transform = `translate(${e.clientX - r.left + 12}px, ${e.clientY - r.top + 12}px)`
    })
    root.querySelectorAll("[data-dfa-improve]").forEach((s) => {
      s.addEventListener("pointerenter", () => { if (!s.classList.contains("is-improved")) cursor.style.opacity = "1" })
      s.addEventListener("pointerleave", hideCursor)
    })
  }

  // ----- Start when in view -----
  if ("IntersectionObserver" in window && !reduce) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { inView = e.isIntersecting; inView ? start() : stop() })
    }, { threshold: 0.35 })
    io.observe(root)
    root._inActionIO = io
  }
  root._inActionStop = () => { stop(); cancelAnims() }
  setup(idx)
}

// "The Problem" ATS scanner: resets to a scanning state, then on scroll-in
// replays scan → count-up → field flips → fail. Static HTML is the END state,
// so JS-off (or reduced-motion) shows the failed result with no animation.
function initAtsProblem() {
  const root = document.querySelector("[data-ats-problem]")
  if (!root || root.dataset.atsBound) return
  root.dataset.atsBound = "1"
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (reduce || !("IntersectionObserver" in window)) return // leave the static end state

  const doc = root.querySelector("[data-dfat-doc]")
  const pctEl = root.querySelector("[data-dfat-pct]")
  const barEl = root.querySelector("[data-dfat-bar]")
  const fields = Array.from(root.querySelectorAll("[data-dfat-field]"))
  const PCT = pctEl ? parseInt(pctEl.textContent, 10) || 0 : 0
  const timers = new Set(), rafs = new Set()
  const clearAll = () => { timers.forEach(clearTimeout); timers.clear(); rafs.forEach(cancelAnimationFrame); rafs.clear() }
  const after = (ms, fn) => { const id = setTimeout(() => { timers.delete(id); fn() }, ms); timers.add(id) }
  const countTo = (el, to, dur) => {
    const t0 = performance.now(); let id
    const tick = (now) => {
      rafs.delete(id)
      const p = Math.min(1, (now - t0) / dur)
      el.textContent = Math.round(to * (1 - Math.pow(1 - p, 3)))
      if (p < 1) { id = requestAnimationFrame(tick); rafs.add(id) }
    }
    id = requestAnimationFrame(tick); rafs.add(id)
  }

  function arm() {
    clearAll()
    root.classList.add("dfat-armed", "dfat-scanning")
    if (doc) doc.classList.remove("is-failed")
    fields.forEach((f) => {
      f.classList.remove("is-ok", "is-bad")
      const dot = f.querySelector("[data-dfat-dot]"); if (dot) dot.textContent = "◍"
      const r = f.querySelector("[data-dfat-result]"); if (r) r.textContent = "Scanning"
    })
    if (pctEl) pctEl.textContent = "0"
    if (barEl) barEl.style.width = "0%"
  }

  function play() {
    root.classList.remove("dfat-armed")           // reveal the tag rows
    if (pctEl) countTo(pctEl, PCT, 1400)
    if (barEl) after(60, () => { barEl.style.width = PCT + "%" })
    fields.forEach((f, i) => after(500 + i * 500, () => {
      const bad = f.dataset.tier === "is-bad"
      f.classList.add(bad ? "is-bad" : "is-ok")
      const dot = f.querySelector("[data-dfat-dot]"); if (dot) dot.textContent = bad ? "✕" : "✓"
      const r = f.querySelector("[data-dfat-result]"); if (r) r.textContent = r.dataset.result
    }))
    after(500 + fields.length * 500 + 400, () => {
      root.classList.remove("dfat-scanning")       // stop scan; chip → Failed
      if (doc) doc.classList.add("is-failed")
    })
  }

  arm()
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { io.disconnect(); play() } })
  }, { threshold: 0.3 })
  io.observe(root)
  root._atsIO = io
  root._atsStop = clearAll
}

// "Resume templates" showcase: category tabs + a native scroll-snap carousel
// (no Swiper on home). Static HTML is the complete end state — the first
// tab's panel is visible and touch/trackpad-scrollable without JS; this only
// wires tab switching and the arrow buttons.
function initTemplates() {
  const root = document.querySelector("[data-templates]")
  if (!root || root.dataset.templatesBound) return
  root.dataset.templatesBound = "true"

  const tabs = Array.prototype.slice.call(root.querySelectorAll("[data-dft-tab]"))
  const panels = Array.prototype.slice.call(root.querySelectorAll("[data-dft-panel]"))
  const prev = root.querySelector("[data-dft-prev]")
  const next = root.querySelector("[data-dft-next]")
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const activeTrack = () => {
    const panel = panels.find((p) => !p.hidden)
    return panel ? panel.querySelector("[data-dft-track]") : null
  }

  const syncArrows = () => {
    const track = activeTrack()
    if (!track || !prev || !next) return
    prev.disabled = track.scrollLeft <= 4
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4
  }

  // Roving tabindex + arrow keys (canonical ARIA tabs pattern, matching the
  // in-action section's tablist behaviour).
  const select = (tab) => {
    tabs.forEach((b) => {
      const on = b === tab
      b.setAttribute("aria-selected", on ? "true" : "false")
      b.setAttribute("tabindex", on ? "0" : "-1")
    })
    panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute("aria-controls") })
    const track = activeTrack()
    if (track) track.scrollTo({ left: 0, behavior: "auto" })
    syncArrows()
  }
  tabs.forEach((tab, i) => {
    tab.setAttribute("tabindex", tab.getAttribute("aria-selected") === "true" ? "0" : "-1")
    tab.addEventListener("click", () => select(tab))
    tab.addEventListener("keydown", (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
      e.preventDefault()
      const target = tabs[(i + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length]
      select(target)
      target.focus()
    })
  })

  const step = (dir) => {
    const track = activeTrack()
    if (!track) return
    const card = track.querySelector(".dft-card")
    const width = card ? card.getBoundingClientRect().width + 24 : 400
    track.scrollBy({ left: dir * width, behavior: reduce ? "auto" : "smooth" })
  }
  if (prev) prev.addEventListener("click", () => step(-1))
  if (next) next.addEventListener("click", () => step(1))

  panels.forEach((p) => {
    const track = p.querySelector("[data-dft-track]")
    if (track) track.addEventListener("scroll", syncArrows, { passive: true })
  })
  syncArrows()
}

// Single centered testimonial: reveal the quote word-by-word when it scrolls
// into view. Pure enhancement — the static markup is already fully visible, so
// on reduced-motion or without IntersectionObserver we leave it untouched.
function initTestimonialQuote() {
  const root = document.querySelector("[data-testimonial-quote]")
  if (!root || root.dataset.testimonialQuoteBound) return
  root.dataset.testimonialQuoteBound = "true"

  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (reduce || !("IntersectionObserver" in window)) return // stays visible

  // If the section is already on screen (e.g. a Turbo back/forward restore that
  // reinstates the scroll position at the bottom of the page), skip the reveal
  // entirely — hiding-then-fading would flash already-visible text.
  const rect = root.getBoundingClientRect()
  if (rect.top < window.innerHeight && rect.bottom > 0) {
    root.classList.add("dfq--anim", "is-visible")
    return
  }

  // Otherwise hide the words now — the section is below the fold, so this
  // happens before it's ever scrolled into view (no flash on first load).
  root.classList.add("dfq--anim")
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return
      root.classList.add("is-visible")
      io.disconnect()
    })
  }, { threshold: 0.35 })
  io.observe(root)
  root._dfqIO = io
}

// Shared scroll-in reveal (Rezi-style fade-up) for home sections. Elements
// marked data-dfh-reveal rise as one; data-dfh-reveal-group staggers its direct
// children. Pure enhancement: the static markup is already visible, so on
// reduced-motion / no IntersectionObserver we leave everything untouched. The
// reveal is a CSS animation removed on animationend, so it never sits on top of
// an element's own hover transform.
function initSectionReveal() {
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (reduce || !("IntersectionObserver" in window)) return
  // Idempotent: bindHomePage() re-runs on turbo:load / turbo:frame-load. Keep
  // the first observer as the live one so teardown's disconnect() targets it.
  if (window.__dfhRevealIO) return

  const targets = Array.prototype.slice.call(document.querySelectorAll("[data-dfh-reveal], [data-dfh-reveal-group]"))
  if (!targets.length) return

  const cleanup = (el) => {
    const done = () => {
      el.classList.remove("dfh-armed", "is-in")
      el.style.animationDelay = ""
      el.removeEventListener("animationend", done)
    }
    el.addEventListener("animationend", done)
  }
  const play = (el) => {
    if (el.hasAttribute("data-dfh-reveal-group")) {
      Array.prototype.slice.call(el.children).forEach((c) => c.classList.add("is-in"))
    } else {
      el.classList.add("is-in")
    }
  }
  const arm = (el) => {
    if (el.hasAttribute("data-dfh-reveal-group")) {
      Array.prototype.slice.call(el.children).forEach((c, i) => {
        c.classList.add("dfh-armed")
        c.style.animationDelay = `${i * 55}ms`
        cleanup(c)
      })
    } else {
      el.classList.add("dfh-armed")
      cleanup(el)
    }
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return
      play(e.target)
      io.unobserve(e.target)
    })
  }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" })

  targets.forEach((el) => {
    if (el.dataset.dfhRevealBound) return
    el.dataset.dfhRevealBound = "true"
    const r = el.getBoundingClientRect()
    // Already in view at init (deep-link / refresh scrolled down, or a pre-JS
    // scroll): leave it visible — don't hide-then-replay a fade-up on content
    // the user is already looking at.
    if (r.top < window.innerHeight && r.bottom > 0) return
    arm(el)
    io.observe(el)
  })
  window.__dfhRevealIO = io
}

function bindHomePage() {
  if (!homeRoot()) return

  installFallbackJobClickHandlers()
  initHeaderScroll()
  bindDropdowns()
  bindFlashDismiss()
  bindAnalyticsSections()
  bindPlanTabs()
  bindJobFilters()
  bindLoadMore()
  initLazyImages()
  initHeroReveal()
  initHeroUpload()
  initHeroShowcase()
  initSocialProof()
  initInAction()
  initAtsProblem()
  initTemplates()
  initTestimonialQuote()
  initSectionReveal()
  scheduleHomepageAnalytics()
  scheduleJobInteractions()
  scheduleHomeJobsRuntime()
  initFaqWhenVisible()
}

function teardownHomePage() {
  teardownFaq()
  teardownJobsRuntimeObserver()
  // The home page is Turbo-cacheable; the restored snapshot loses JS listeners.
  // Clear the bind guards so initHero* re-attach on the next bindHomePage().
  const heroTitle = document.querySelector("[data-hero-title]");
  if (heroTitle) delete heroTitle.dataset.heroRevealBound;
  const heroUpload = document.querySelector("[data-hero-upload]");
  if (heroUpload) delete heroUpload.dataset.heroUploadBound;
  const heroShowcase = document.querySelector("[data-hero-showcase]");
  if (heroShowcase) {
    if (heroShowcase._heroShowcaseStop) heroShowcase._heroShowcaseStop();
    delete heroShowcase.dataset.heroShowcaseBound;
  }
  const socialProof = document.querySelector("[data-social-proof]");
  if (socialProof) {
    if (socialProof._socialProofIO) socialProof._socialProofIO.disconnect();
    if (socialProof._tcStop) socialProof._tcStop();
    delete socialProof.dataset.socialProofBound;
    const car = socialProof.querySelector("[data-testimonial-carousel]");
    if (car) delete car.dataset.tcBound;
  }
  const inAction = document.querySelector("[data-in-action]");
  if (inAction) {
    if (inAction._inActionIO) inAction._inActionIO.disconnect();
    if (inAction._inActionStop) inAction._inActionStop();
    delete inAction.dataset.inActionBound;
  }
  const atsProblem = document.querySelector("[data-ats-problem]");
  if (atsProblem) {
    if (atsProblem._atsIO) atsProblem._atsIO.disconnect();
    if (atsProblem._atsStop) atsProblem._atsStop();
    delete atsProblem.dataset.atsBound;
  }
  const templates = document.querySelector("[data-templates]");
  if (templates) delete templates.dataset.templatesBound;
  const testimonialQuote = document.querySelector("[data-testimonial-quote]");
  if (testimonialQuote) {
    if (testimonialQuote._dfqIO) testimonialQuote._dfqIO.disconnect();
    testimonialQuote.classList.remove("dfq--anim", "is-visible");
    delete testimonialQuote.dataset.testimonialQuoteBound;
  }
  if (window.__dfhRevealIO) { window.__dfhRevealIO.disconnect(); window.__dfhRevealIO = null; }
  document.querySelectorAll("[data-dfh-reveal], [data-dfh-reveal-group]").forEach((el) => {
    delete el.dataset.dfhRevealBound;
    const targets = el.hasAttribute("data-dfh-reveal-group")
      ? Array.prototype.slice.call(el.children)
      : [el];
    targets.forEach((t) => { t.classList.remove("dfh-armed", "is-in"); t.style.animationDelay = ""; });
  });
}

// The home page ships the lean `home` pack, which does NOT start Stimulus.
// Start a minimal app that registers only the nav controller so the
// redesigned navbar (dropdowns / scroll shadow / mobile overlay) works here.
function initNavController() {
  if (window.__nav2StimulusStarted) return
  if (!document.querySelector('[data-controller~="nav"]')) return
  const app = Application.start()
  app.register("nav", NavController)
  window.__nav2StimulusStarted = true
}
document.addEventListener("DOMContentLoaded", initNavController)
document.addEventListener("turbo:load", initNavController)
document.addEventListener("DOMContentLoaded", bindHomePage)
document.addEventListener("turbo:load", bindHomePage)
document.addEventListener("turbo:render", bindHomePage)
document.addEventListener("turbo:frame-load", bindHomePage)
document.addEventListener("turbo:before-cache", teardownHomePage)

document.addEventListener("DOMContentLoaded", bindJobWorldView)
document.addEventListener("turbo:load", bindJobWorldView)
document.addEventListener("turbo:frame-load", bindJobWorldView)
